import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import socket, { ensureSocketReady } from '../socket';
import { useAuth } from '../context/AuthContext';
import Input from './ui/Input';
import Select from './ui/Select';
import Button from './ui/Button';
import Card from './ui/Card';
import { MAX_USER_NAME_LENGTH, clampUserName } from '../constants/limits';

function JoinRoom() {
    const { user } = useAuth();
    const [roomId, setRoomId] = useState('');
    const [userName, setUserName] = useState(() => clampUserName(user?.name || ''));
    const [role, setRole] = useState('DEV');
    const [isJoining, setIsJoining] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        if (user?.name) {
            setUserName((prev) => prev || clampUserName(user.name));
        }
    }, [user]);

    useEffect(() => {
        const onAuthError = ({ message }) => {
            alert(message || 'Faça login novamente para entrar na sala.');
        };
        socket.on('authError', onAuthError);
        return () => socket.off('authError', onAuthError);
    }, []);

    const handleJoinSubmit = async (e) => {
        e.preventDefault();
        const trimmedName = clampUserName(userName);
        if (!roomId.trim() || !trimmedName) {
            alert('Informe o ID da sala e seu nome.');
            return;
        }

        setIsJoining(true);

        try {
            await ensureSocketReady();

            const normalizedRoomId = roomId.trim().toUpperCase();

            socket.emit('checkRoomExists', normalizedRoomId, ({ exists }) => {
                if (!exists) {
                    setIsJoining(false);
                    alert('Sala não encontrada ou expirada.');
                    return;
                }

                const normalizedRole = (role || 'OUTROS').toUpperCase();

                localStorage.setItem('roomId', normalizedRoomId);
                localStorage.setItem('userName', trimmedName);
                localStorage.setItem('userRole', normalizedRole);

                // joinRoom é feito apenas em PokerRoom (listeners + ACK)
                navigate(`/room/${normalizedRoomId}`);
                setIsJoining(false);
            });
        } catch {
            setIsJoining(false);
            alert('Não foi possível conectar ao servidor. Tente novamente.');
        }
    };

    const voltarHome = () => navigate('/');

    return (
        <div className="flex items-center justify-center w-full max-w-md mx-auto mt-8 z-10">
            <Card className="p-8 w-full">
                <h2 className="text-2xl font-bold text-slate-100 mb-6 text-center">
                    🔑 Entrar em Sala
                </h2>

                <form onSubmit={handleJoinSubmit} className="space-y-5">
                    <Input
                        label="ID da Sala"
                        type="text"
                        placeholder="Digite o ID da sala"
                        value={roomId}
                        onChange={(e) => setRoomId(e.target.value.toUpperCase())}
                        maxLength={12}
                        required
                    />

                    <Input
                        label="Seu Nome"
                        type="text"
                        placeholder="Digite seu nome"
                        value={userName}
                        onChange={(e) => setUserName(e.target.value)}
                        maxLength={MAX_USER_NAME_LENGTH}
                        required
                    />

                    <Select
                        label="Seu Papel"
                        value={role}
                        onChange={(e) => setRole(e.target.value)}
                    >
                        <option value="DEV">Dev</option>
                        <option value="QA">QA</option>
                        <option value="DEVOPS">DevOps</option>
                        <option value="PO">PO</option>
                        <option value="LEADER">Leader</option>
                        <option value="OUTROS">Outros</option>
                    </Select>

                    <div className="flex flex-col sm:flex-row gap-3 pt-4">
                        <Button
                            variant="secondary"
                            type="submit"
                            disabled={isJoining}
                            loading={isJoining}
                            className="flex-1"
                        >
                            {isJoining ? 'Entrando...' : '🚪 Entrar'}
                        </Button>
                        <Button
                            variant="ghost"
                            type="button"
                            onClick={voltarHome}
                            className="flex-1"
                        >
                            ← Voltar
                        </Button>
                    </div>
                </form>
            </Card>
        </div>
    );
}

export default JoinRoom;
